import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Save,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  AddTrackingThunk,
  GetSingleTrackingThunk,
} from "../../redux/states/tracking/thunk";

const CreateUpdateOrderTracking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tracking_number } = useParams();

  const [formData, setFormData] = useState({
    order_id: "",
    tracking_number: "",
    current_status: "pending",
    courier_name: "",
    current_location: "",
    remarks: "",
  });

  const [loading, setLoading] = useState(false);
  const [singleTracking, setSingleTracking] = useState(null);

  // ✅ Fetch existing tracking (Edit Mode)
  useEffect(() => {
    if (tracking_number) {
      setLoading(true);
      dispatch(GetSingleTrackingThunk(tracking_number))
        .unwrap()
        .then((res) => {
          setSingleTracking(res);
          setFormData({
            order_id: res.data.tracking.order_id || "",
            tracking_number: res.data.tracking.tracking_number || "",
            current_status: res.data.tracking.current_status || "pending",
            courier_name: res.data.tracking.courier_name || "",
            current_location: res.data.tracking.current_location || "",
            remarks: res.data.tracking.remarks || "",
          });
        })
        .catch(() => toast.error("Failed to load tracking data"))
        .finally(() => setLoading(false));
    }
  }, [dispatch, tracking_number]);

  // ✅ Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await dispatch(AddTrackingThunk(formData)).unwrap();
      toast.success("Tracking details saved successfully!");
      navigate("/order-tracking");
    } catch (err) {
      toast.error(err || "Failed to save tracking details");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Status options
  const statusOptions = [
    "pending",
    "processing",
    "shipped",
    "in_transit",
    "out_for_delivery",
    "delivered",
    "cancelled",
    "returned",
    "failed",
  ];

  if (loading && tracking_number) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading tracking details...</p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  return (
    <NonAuthLayout>
      <div className="min-h-screen py-2 px-2 sm:px-2 lg:px-2">
        <div className="max-w-12xl mx-auto bg-white shadow-lg rounded-2xl p-4 border border-gray-100">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                {tracking_number ? "Update Tracking" : "Add New Tracking"}
              </h1>
              <p className="text-gray-500 mt-1">
                {tracking_number
                  ? "Edit tracking details for this order"
                  : "Add a new tracking record for an order"}
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Order ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order ID
              </label>
              <input
                type="text"
                name="order_id"
                value={formData.order_id}
                onChange={handleChange}
                placeholder="Enter order ID"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Tracking Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tracking Number
              </label>
              <input
                type="text"
                name="tracking_number"
                value={formData.tracking_number}
                onChange={handleChange}
                placeholder="Enter tracking number"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Courier Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Courier Name
              </label>
              <input
                type="text"
                name="courier_name"
                value={formData.courier_name}
                onChange={handleChange}
                placeholder="Enter courier company name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Current Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Location
              </label>
              <input
                type="text"
                name="current_location"
                value={formData.current_location}
                onChange={handleChange}
                placeholder="Enter current package location"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Current Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Status
              </label>
              <select
                name="current_status"
                value={formData.current_status}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status.replace(/_/g, " ").toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remarks
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows="4"
                placeholder="Enter any remarks or notes..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              ></textarea>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {tracking_number ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {tracking_number ? "Update Tracking" : "Add Tracking"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default CreateUpdateOrderTracking;
